/**
 * alertDetailsApi.js — API functions for the Alert Details Report page.
 *
 * fetchEventReport   — runs the full event report query
 * fetchFilterOptions — loads all 11 filter option sets
 * fetchPreformNotes  — preformatted note templates
 * addEventNote       — add note to event
 * editEventReason    — change event reason code
 */

import { apiFetch, apiSave, apiTransaction } from '../../../../services/api.js';

// ---------------------------------------------------------------------------
// Internals (reuse api.js patterns for the report endpoint)
// ---------------------------------------------------------------------------

const getBaseUrl = () =>
    import.meta.env.VITE_DEVMODE === 'true'
        ? import.meta.env.VITE_DEVAPI
        : import.meta.env.VITE_API;

const getHeaders = () => ({
    'Content-Type': 'application/json',
    'x-amz-security-token': import.meta.env.VITE_AUTH_TOKEN,
});

// ---------------------------------------------------------------------------
// Fetch Event Report
// ---------------------------------------------------------------------------

/**
 * Runs the eventReport query with all filter fields.
 *
 * Uses a direct POST to the report endpoint because the V2 apiReport()
 * is missing fields required by EventReports (resident_group_filter,
 * area_group_filter, user_group_filter, resp_filter_items, username).
 *
 * @param {object} opts
 * @param {number} opts.campusId
 * @param {string} opts.username
 * @param {string} opts.startDate   — ISO format (yyyy-mm-dd hh:mm:ss)
 * @param {string} opts.endDate     — ISO format
 * @param {object} opts.filters     — all report-level filter selections
 * @returns {Promise<object>} — { records, summary, overlimit, url, ErrorCode }
 */
export async function fetchEventReport({ campusId, username, startDate, endDate, filters = {} }) {
    const body = {
        rep_type: 'eventReport',
        username: username || '',
        campus_id: campusId,
        start_date: startDate,
        end_date: endDate,
        event_category_filter: '',
        device_category_filter: filters.deviceCategories || '',
        device_filter: filters.devices || '',
        area_type_filter: filters.areaTypes || '',
        reason_filter: filters.reasons || '',
        user_filter: filters.users || '',
        event_type_filter: filters.eventTypes || '',
        resident_filter: filters.residents || '',
        resident_group_filter: filters.residentGroups || '',
        area_filter: filters.areas || '',
        area_group_filter: filters.areaGroups || '',
        user_group_filter: filters.userGroups || '',
        resp_filter_items: filters.responseTime || [false, 'lt', 60],
    };

    try {
        const resp = await fetch(getBaseUrl() + import.meta.env.VITE_MY_REPORTS_ENDPOINT, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(body),
        });

        if (resp.status === 503) return { ErrorCode: 11 };
        if (resp.status === 500) return { ErrorCode: 10 };
        if (resp.status === 403) return { ErrorCode: 12 };

        const raw = await resp.json();

        if (raw?.ErrorCode) {
            return { ErrorCode: raw.ErrorCode };
        }

        // Report endpoint wraps data in { Status, Res: {...} }
        const data = raw?.Res || raw;

        if (data?.ErrorCode) {
            return { ErrorCode: data.ErrorCode };
        }

        return {
            records: data?.records || [],
            summary: data?.summary || {},
            overlimit: Boolean(data?.overlimit),
            url: data?.url || '',
        };
    } catch (err) {
        console.error('[alertDetailsApi] fetchEventReport error:', err);
        return { ErrorCode: 10 };
    }
}

// ---------------------------------------------------------------------------
// Fetch Filter Options
// ---------------------------------------------------------------------------

/**
 * Loads all filter option datasets in parallel.
 *
 * 1. getportalfilters — areas, areaGroups, areaTypes, residents, residentGroups,
 *                        users, userGroups, deviceCategories
 * 2. att_event_types  — event types
 * 3. core_reason_codes — reason codes
 * 4. sp_dev_getInovonicsDevices — devices
 *
 * @param {number} campusId
 * @returns {Promise<object>}
 */
export async function fetchFilterOptions(campusId) {
    try {
        const [portalFilters, eventTypes, reasonCodes, devices] = await Promise.all([
            // 1. Portal filters (areas, areaGroups, areaTypes, residents, etc.)
            fetchPortalFilters(campusId),
            // 2. Event types — direct table fetch
            apiFetch({
                table: 'att_event_types',
                procedure: 'fetchall',
                reqType: 'direct',
                pKey: 'campus_id',
                pVal: campusId,
            }),
            // 3. Reason codes — direct table fetch
            apiFetch({
                table: 'core_reason_codes',
                procedure: 'fetchbyparameter',
                reqType: 'direct',
                pKey: 'campus_id',
                pVal: campusId,
            }),
            // 4. Devices — stored procedure
            apiFetch({
                procedure: 'sp_dev_getInovonicsDevices',
                reqType: 'stored',
                payload: {
                    device_filter: '',
                    device_category_filter: '1,2,3,6,7,8,9,10,11,12,15,16,17',
                    campus_id: campusId,
                },
            }),
        ]);

        // Add synthetic "No Reason" option (id=0) to reason codes
        const reasonsWithNoReason = [
            { reason_code_id: 0, reason_code_description: 'No Reason' },
            ...(reasonCodes || []),
        ];

        // Residents come back with resident_first_name + resident_last_name
        // but the filter drawer expects a single resident_name field.
        const residentsWithName = (portalFilters.residents || []).map((r) => ({
            ...r,
            resident_name: `${r.resident_first_name || ''} ${r.resident_last_name || ''}`.trim() || '(unknown)',
        }));

        return {
            areas: portalFilters.areas || [],
            areaGroups: portalFilters.areaGroups || [],
            areaTypes: portalFilters.areaTypes || [],
            residents: residentsWithName,
            residentGroups: portalFilters.residentGroups || [],
            users: portalFilters.users || [],
            userGroups: portalFilters.userGroups || [],
            deviceCategories: portalFilters.deviceCategories || [],
            devices: devices || [],
            eventTypes: eventTypes || [],
            reasonCodes: reasonsWithNoReason,
        };
    } catch (err) {
        console.error('[alertDetailsApi] fetchFilterOptions error:', err);
        return {};
    }
}

/**
 * Internal — calls getportalfilters and normalizes the result keys.
 */
async function fetchPortalFilters(campusId) {
    const body = {
        rep_type: 'getportalfilters',
        campus_id: campusId,
        device_category_filter: '1',
        area_type_filter: '1',
        user_filter: '1',
        resident_group_filter: '1',
        area_filter: '0',
        area_group_filter: '1',
        user_group_filter: '1',
        resident_filter: '1',
    };

    try {
        const resp = await fetch(getBaseUrl() + import.meta.env.VITE_MY_REPORTS_ENDPOINT, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(body),
        });

        const raw = await resp.json();

        // Report endpoint wraps data in { Status, Res: {...} }
        const data = raw?.Res || raw;

        return {
            areas: data?.areas || [],
            areaGroups: data?.area_groups || [],
            areaTypes: data?.area_types || [],
            residents: data?.residents || [],
            residentGroups: data?.resident_groups || [],
            users: data?.users || [],
            userGroups: data?.user_groups || [],
            deviceCategories: data?.device_categories || [],
        };
    } catch (err) {
        console.error('[alertDetailsApi] fetchPortalFilters error:', err);
        return {};
    }
}

// ---------------------------------------------------------------------------
// Fetch Preformatted Notes
// ---------------------------------------------------------------------------

/**
 * @param {number} campusId
 * @returns {Promise<Array>}
 */
export async function fetchPreformNotes(campusId) {
    return apiFetch({
        table: 'core_preform_event_notes',
        procedure: 'fetchbyparameter',
        reqType: 'direct',
        pKey: 'campus_id',
        pVal: campusId,
    });
}

// ---------------------------------------------------------------------------
// Add Event Note
// ---------------------------------------------------------------------------

/**
 * @param {number} eventId
 * @param {number} campusId
 * @param {string} note
 * @param {string} userId
 * @param {string} clientId
 * @returns {Promise<[boolean, string?]>}
 */
export async function addEventNote(eventId, campusId, note, userId, clientId) {
    return apiSave({
        table: 'core_event_notes',
        payload: [{ event_id: eventId, campus_id: campusId, note, modified_by: userId }],
        method: 'PUT',
        pKey: 'note_id',
        condition: 'primary',
        campusId,
        clientId,
        userId,
    });
}

// ---------------------------------------------------------------------------
// Edit Event Reason
// ---------------------------------------------------------------------------

/**
 * @param {number} eventId
 * @param {number} newReasonId
 * @param {number} oldReasonId
 * @param {number} campusId
 * @param {string} userId
 * @param {string} clientId
 * @returns {Promise<[boolean, string?]>}
 */
export async function editEventReason(eventId, newReasonId, oldReasonId, campusId, userId, clientId) {
    return apiTransaction({
        transType: 'event_reason_edit',
        payload: [{ event_id: eventId, reason_id: newReasonId }],
        oldPayload: [{ event_id: eventId, reason_id: oldReasonId }],
        campusId,
        userId,
        clientId,
    });
}
